import { Button, Chip, Select, Textarea } from '@emailforge/ui';
import { useMemo, useState } from 'react';

import { Icon } from '@/components/ui/icons';
import {
  type Approval,
  type ApprovalStatus,
  useCancelApprovalMutation,
  useDecideApprovalMutation,
  useListApprovalsQuery,
  useRequestApprovalMutation,
} from '@/features/collab/approvalsApi';
import { useListMembersQuery } from '@/features/members/membersApi';
import { useAppSelector } from '@/store/hooks';

/**
 * Editor tab that exposes the per-version approval workflow:
 *
 *   - The author requests approval, optionally targeting a specific approver.
 *   - The approver clicks Approve/Reject (with an optional note).
 *   - The author can cancel a pending request.
 *
 * Backend invariant (enforced server-side): only one `pending` approval per
 * version at a time. We hide the request form when one is open.
 *
 * Mounted as a tab next to Inspector/Comments in the editor's right sidebar.
 */

type Props = { versionId: string };

export function ApprovalsPanel({ versionId }: Props) {
  const currentUserId = useAppSelector((s) => s.auth.user?.id ?? '');
  const { data: approvals = [], isLoading } = useListApprovalsQuery(versionId);
  const { data: members = [] } = useListMembersQuery();
  const [requestApproval, requestMut] = useRequestApprovalMutation();
  const [decide, decideMut] = useDecideApprovalMutation();
  const [cancel, cancelMut] = useCancelApprovalMutation();
  const [requestNote, setRequestNote] = useState('');
  const [requestApproverId, setRequestApproverId] = useState<string>('');
  const [decisionNote, setDecisionNote] = useState('');

  const pending = useMemo(() => approvals.find((a) => a.status === 'pending'), [approvals]);
  const history = useMemo(() => approvals.filter((a) => a !== pending), [approvals, pending]);

  const memberById = useMemo(() => new Map(members.map((m) => [m.userId, m])), [members]);
  const memberLabel = (id: string | null) => {
    if (!id) return 'anyone';
    const m = memberById.get(id);
    return m ? (m.user.name ?? m.user.email) : id.slice(0, 8) + '…';
  };

  const onRequest = async () => {
    await requestApproval({
      versionId,
      approverId: requestApproverId || undefined,
      note: requestNote || undefined,
    }).unwrap();
    setRequestNote('');
    setRequestApproverId('');
  };

  const onDecide = async (decision: 'approved' | 'rejected') => {
    if (!pending) return;
    await decide({
      versionId,
      approvalId: pending.id,
      decision,
      note: decisionNote || undefined,
    }).unwrap();
    setDecisionNote('');
  };

  const onCancel = async () => {
    if (!pending) return;
    await cancel({ versionId, approvalId: pending.id }).unwrap();
  };

  const canDecide = pending && pending.requestedBy !== currentUserId;
  const canCancel = pending && pending.requestedBy === currentUserId;

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div
        className="mono"
        style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.08, color: 'var(--ink-3)' }}
      >
        Approvals · v{versionShort(versionId)}
      </div>

      {pending && (
        <div
          className="mf-card"
          style={{
            padding: 14,
            background: 'var(--flame-soft)',
            borderColor: 'var(--flame-line)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Chip variant="flame">pending
            </Chip>
            <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
              {new Date(pending.createdAt).toLocaleString()}
            </span>
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 8 }}>
            <strong>{memberLabel(pending.requestedBy)}</strong> requested approval from{' '}
            <strong>{memberLabel(pending.approverId)}</strong>.
          </div>
          {pending.note && (
            <div style={{ fontSize: 12, color: 'var(--ink-2)', fontStyle: 'italic', marginBottom: 10 }}>
              "{pending.note}"
            </div>
          )}

          {canDecide && (
            <>
              <Textarea
                
                value={decisionNote}
                onChange={(e) => setDecisionNote(e.target.value)}
                placeholder="Optional note for the requester…"
                rows={2}
                style={{ fontSize: 12, marginBottom: 8 }} />
              <div style={{ display: 'flex', gap: 6 }}>
                <Button variant="flame" size="sm" onClick={() => onDecide('approved')}
                  disabled={decideMut.isLoading}
                >
                  <Icon.check size={12} /> Approve
                </Button>
                <Button size="sm" onClick={() => onDecide('rejected')}
                  disabled={decideMut.isLoading}
                >
                  <Icon.x size={12} /> Reject
                </Button>
              </div>
            </>
          )}
          {canCancel && (
            <Button variant="ghost" size="sm" onClick={onCancel}
              disabled={cancelMut.isLoading}
              style={{ marginTop: 6 }}>
              Cancel request
            </Button>
          )}
        </div>
      )}

      {!pending && (
        <div className="mf-card" style={{ padding: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 550, marginBottom: 6 }}>Request approval</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.5, marginBottom: 10 }}>
            Lock this version behind a teammate's review. Publish is blocked while a request is
            pending.
          </div>
          <div
            className="mono"
            style={{
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: 0.06,
              color: 'var(--ink-3)',
              marginBottom: 4,
            }}
          >
            Approver
          </div>
          <Select
            
            style={{ marginBottom: 8 }}
            value={requestApproverId}
            onChange={(e) => setRequestApproverId(e.target.value)}>
            <option value="">Anyone with permission</option>
            {members
              .filter((m) => m.userId !== currentUserId)
              .map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.user.name ?? m.user.email}
                </option>
              ))}
          </Select>
          <Textarea
            
            value={requestNote}
            onChange={(e) => setRequestNote(e.target.value)}
            placeholder="Anything the approver should know…"
            rows={2}
            style={{ fontSize: 12, marginBottom: 8 }} />
          <Button variant="flame" size="sm" onClick={onRequest}
            disabled={requestMut.isLoading}
            style={{ width: '100%', justifyContent: 'center' }}>
            <Icon.send size={12} /> {requestMut.isLoading ? 'Requesting…' : 'Request approval'}
          </Button>
        </div>
      )}

      {history.length > 0 && (
        <div>
          <div
            className="mono"
            style={{
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: 0.08,
              color: 'var(--ink-3)',
              marginBottom: 6,
            }}
          >
            History
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {history.map((a) => (
              <ApprovalHistoryRow
                key={a.id}
                approval={a}
                requesterLabel={memberLabel(a.requestedBy)}
                approverLabel={memberLabel(a.approverId)}
              />
            ))}
          </div>
        </div>
      )}

      {isLoading && approvals.length === 0 && (
        <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
          Loading…
        </div>
      )}
    </div>
  );
}

function ApprovalHistoryRow({
  approval,
  requesterLabel,
  approverLabel,
}: {
  approval: Approval;
  requesterLabel: string;
  approverLabel: string;
}) {
  return (
    <div
      className="mf-card"
      style={{
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Chip variant={chipVariant(approval.status)}>{approval.status}</Chip>
        <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
          {(approval.decidedAt ?? approval.createdAt) &&
            new Date(approval.decidedAt ?? approval.createdAt).toLocaleString()}
        </span>
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.4 }}>
        {requesterLabel} → {approverLabel}
      </div>
      {approval.note && (
        <div style={{ fontSize: 11.5, color: 'var(--ink-3)', fontStyle: 'italic' }}>
          "{approval.note}"
        </div>
      )}
    </div>
  );
}

function chipVariant(status: ApprovalStatus): 'live' | 'err' | 'default' | 'flame' {
  switch (status) {
    case 'approved':
      return 'live';
    case 'rejected':
      return 'err';
    case 'cancelled':
      return 'default';
    default:
      return 'flame';
  }
}

function versionShort(id: string): string {
  return id.length >= 8 ? id.slice(0, 8) : id;
}
